export class ValidatorField {
  // Patterns para validação de campos
  static readonly USERNAME_SPECIAL_CHARS = /[\s.,\-/()]/g;
  static readonly EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  static readonly ONLY_NUMBERS = /\D/g;
  static readonly PHONE = /^(\d{2})(9\d{8})$/;
  
  // Patterns para formatação
  static readonly REMOVE_WHITESPACE = /\s/g;
  static readonly REMOVE_SPECIAL_CHARS = /[^\w\s]/g;
  
  // Patterns para normalização de URLs
  static readonly TRAILING_SLASH = /\/+$/;
  static readonly LEADING_SLASH = /^\/+/;
  
  // Patterns para validação de documentos
  static readonly CPF_LENGTH = /^\d{11}$/;
  static readonly CPF_MASK = /^(\d{3})\.(\d{3})\.(\d{3})-(\d{2})$/;
  static readonly CNPJ_LENGTH = /^\d{14}$/;
  static readonly REPEATED_DIGITS = /^(\d)\1+$/;
  
  // Métodos utilitários
  static removeSpecialCharsFromUsername(value: string): string {
    if (value.includes('@')) {
      return value.replace(/\s/g, '');
    }
    
    return value.replace(this.USERNAME_SPECIAL_CHARS, '');
  }
  
  static isValidEmail(email: string): boolean {
    return this.EMAIL.test(email);
  }
  
  static extractNumbers(value: string): string {
    return value.replace(this.ONLY_NUMBERS, '');
  }
  
  static validatePhone(phone: string): boolean {
    const numbers = this.extractNumbers(phone);
    
    if (!this.PHONE.test(numbers)) {
      return false;
    }
    
    // Extrai o DDD
    const ddd = numbers.substring(0, 2);
    
    // Lista de DDDs válidos do Brasil
    const validDDDs = [
      '11', '12', '13', '14', '15', '16', '17', '18', '19',
      '21', '22', '24', '27', '28',
      '31', '32', '33', '34', '35', '37', '38',
      '41', '42', '43', '44', '45', '46', '47', '48', '49',
      '51', '53', '54', '55',
      '61', '62', '63', '64', '65', '66', '67', '68', '69',
      '71', '73', '74', '75', '77', '79',
      '81', '82', '83', '84', '85', '86', '87', '88', '89',
      '91', '92', '93', '94', '95', '96', '97', '98', '99'
    ];
    
    return validDDDs.includes(ddd);
  }
  
  // static formatPhone(phone: string): string {
  //     const numbers = this.extractNumbers(phone);
  //
  //     if (this.PHONE.test(numbers)) {
  //         const ddd = numbers.substring(0, 2);
  //         const numero = numbers.substring(2);
  //
  //         if (numero.length === 9) {
  //             return `(${ddd}) ${numero.substring(0, 5)}-${numero.substring(5)}`;
  //         }
  //
  //         return `(${ddd}) ${numero.substring(0, 4)}-${numero.substring(4)}`;
  //     }
  //
  //     return numbers;
  // }
  
  static formatPhone(phone: string): string {
    const numbers = this.extractNumbers(phone);
    
    if (this.PHONE.test(numbers)) {
      const ddd = numbers.substring(0, 2);
      const numero = numbers.substring(2);
      
      // Como agora só aceitamos números com 9 dígitos após o DDD
      return `(${ddd}) ${numero.substring(0, 5)}-${numero.substring(5)}`;
    }
    
    return numbers;
  }
  
  static validateCPF(value: string): boolean {
    if (!value) return false;
    
    // Remove espaços extras e verifica se o CPF está no formato correto com máscara
    const trimmedValue = value.trim();
    if (!this.CPF_MASK.test(trimmedValue)) {
      return false;
    }
    
    // Remove todos os caracteres não numéricos para validação
    const cpf = trimmedValue.replace(/\D/g, '');
    
    if (this.REPEATED_DIGITS.test(cpf)) {
      return false;
    }
    
    let sum = 0;
    let remainder;
    
    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
    
    remainder = (sum * 10) % 11;
    
    if (remainder === 10 || remainder === 11) remainder = 0;
    
    if (remainder !== parseInt(cpf.substring(9, 10))) {
      return false;
    }
    
    sum = 0;
    
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
    
    remainder = (sum * 10) % 11;
    
    if (remainder === 10 || remainder === 11) remainder = 0;
    
    if (remainder !== parseInt(cpf.substring(10, 11))) {
      return false;
    }
    
    return true;
  }
  
  static validateCNPJ(value: string): boolean {
    if (!value) return false;
    
    const cnpj = value.replace(/\D/g, '');
    
    if (!this.CNPJ_LENGTH.test(cnpj)) {
      return false;
    }
    
    if (this.REPEATED_DIGITS.test(cnpj)) {
      return false;
    }
    
    let size = cnpj.length - 2;
    let numbers = cnpj.substring(0, size);
    const digits = cnpj.substring(size);
    let sum = 0;
    let pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      
      if (pos < 2) pos = 9;
    }
    
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    
    if (result !== parseInt(digits.charAt(0))) {
      return false;
    }
    
    size = size + 1;
    numbers = cnpj.substring(0, size);
    sum = 0;
    pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      
      if (pos < 2) pos = 9;
    }
    
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    
    if (result !== parseInt(digits.charAt(1))) {
      return false;
    }
    
    return true;
  }
  
  static validateBirthdate(value: string): boolean {
    const birthdate = new Date(value);
    const today = new Date();
    const minAge = 10;
    
    // Calcula a diferença em anos
    let age = today.getFullYear() - birthdate.getFullYear();
    const monthDiff = today.getMonth() - birthdate.getMonth();
    
    // Ajusta a idade se ainda não chegou no mês do aniversário
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthdate.getDate())) {
      age--;
    }
    
    return age >= minAge;
  }
  
  static validator = (_: any, value: string, field: any) => {
    if (!value) return Promise.resolve();
    const fieldName = `${field?.name}${field?.label}`;
    const {isValid, errorMessage} = this.validadeField(value, fieldName);
    
    return isValid ? Promise.resolve() : Promise.reject(errorMessage);
  };
  
  static validadeField(value: string, fieldName: string): { isValid: boolean; errorMessage: string } {
    if (!value) return {isValid: false, errorMessage: ''};
    
    fieldName = `${fieldName.toLowerCase()}`;
    
    if (fieldName.includes('cpf')) {
      return {
        isValid: this.validateCPF(value),
        errorMessage: 'CPF inválido'
      };
    }
    
    if (fieldName.includes('cnpj')) {
      return {
        isValid: this.validateCNPJ(value),
        errorMessage: 'CNPJ inválido'
      };
    }
    
    if (fieldName.includes('email') || fieldName.includes('e-mail')) {
      return {
        isValid: this.isValidEmail(value),
        errorMessage: 'E-mail inválido'
      };
    }
    
    if (fieldName.includes('birthdate') || fieldName.includes('nascimento')) {
      return {
        isValid: this.validateBirthdate(value),
        errorMessage: 'A idade deve ser maior que 10 anos'
      };
    }
    
    if (fieldName.includes('phone')) {
      return {
        isValid: this.validatePhone(value),
        errorMessage: 'Telefone inválido. Digite um número de celular válido com DDD'
      };
    }
    
    return {isValid: true, errorMessage: ''};
  }
}
